import { isPlatformBrowser } from '@angular/common';
import { Component, computed, DOCUMENT, inject, isDevMode, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CookieBanner, CookieBannerStore } from '@notfall/cookie-banner';
import { TestStageIndicatorComponent } from '@notfall/ui-components';
import { CanonicalService } from '@notfall/util-angular';
import { filter, map, startWith } from 'rxjs';

@Component({
  imports: [RouterModule, CookieBanner, TestStageIndicatorComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  readonly cookieBannerStore = inject(CookieBannerStore);
  readonly #router = inject(Router);
  readonly #canonicalService = inject(CanonicalService);
  readonly #meta = inject(Meta);
  readonly #platform = inject(PLATFORM_ID);
  readonly #DOCUMENT = inject(DOCUMENT);

  readonly #url = toSignal(
    this.#router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.#router.url),
    ),
    { initialValue: this.#router.url },
  );

  readonly #isEmbed = computed(() => {
    const query = this.#url().split('?')[1] ?? '';
    return new URLSearchParams(query).get('embed') === 'true';
  });

  readonly showTestStage = computed(() => isDevMode() && !this.#isEmbed());

  readonly showCookieBanner = computed(() =>
    this.cookieBannerStore.showCookieBanner() && !this.#isEmbed(),
  );

  constructor() {
    if (isDevMode()) {
      // <meta name="robots" content="noindex, nofollow">
      this.#meta.addTag({ name: 'robots', content: 'noindex, nofollow' });
    }

    if (isPlatformBrowser(this.#platform)) {
      this.#router.events.subscribe(ev => {
        if (ev instanceof NavigationEnd) {
          const host = this.#DOCUMENT.defaultView?.window.location?.host ?? 'notfall-finder.ch';
          this.#canonicalService.setCanonical(
            'https://' + host + ev.urlAfterRedirects);
        }
      });
    }
  }
}
